import type { InstantSearchProps } from "react-instantsearch";
import {
  LEGACY_SEARCH_PARAM_KEYS,
  parseCanonicalList,
  serializeCanonicalList,
  splitUrl,
  WHEN_PARAM_KEYS,
  WHERE_PARAM_KEYS,
  WHO_PARAM_KEY,
} from "@/lib/search-params";
import { FORMAT_OPTIONS, INFLUENCE_FILTERS } from "./components/ProgramsSearch/constants";

type UiState = NonNullable<InstantSearchProps["initialUiState"]>;
type IndexUiState = UiState[string];

/**
 * /programs URL grammar for the modal filters (Where/When/Who ride the shared
 * grammar in `lib/search-params`):
 *
 *   ?availability=sessions&recommended=drop_off,favorites&formats=popup&influences=montessori&languages=en,es
 *
 * These params map onto InstantSearch uiState; Where/When/Who are page-local
 * state serialized by `ProgramsUrlSync` alongside them.
 */

const AVAILABILITY_PARAM = "availability";
const RECOMMENDED_PARAM = "recommended";
// Plural in the URL because Rails reserves `format` for response-format
// negotiation: a full-page load of `?format=popup` would 406.
const FORMATS_PARAM = "formats";
const INFLUENCES_PARAM = "influences";
const LANGUAGES_PARAM = "languages";

export const PROGRAMS_SEARCH_PARAM_KEYS = [
  ...WHERE_PARAM_KEYS,
  ...WHEN_PARAM_KEYS,
  WHO_PARAM_KEY,
  AVAILABILITY_PARAM,
  RECOMMENDED_PARAM,
  FORMATS_PARAM,
  INFLUENCES_PARAM,
  LANGUAGES_PARAM,
  ...LEGACY_SEARCH_PARAM_KEYS,
] as const;

// URL token ↔ toggle facet attribute. American spelling in the URL (favorites)
// per repo convention, whatever the internal label keys say.
const RECOMMENDED_TOKENS = ["drop_off", "community", "favorites", "featured"] as const;
type RecommendedToken = (typeof RECOMMENDED_TOKENS)[number];
const TOKEN_TO_TOGGLE: Record<RecommendedToken, string> = {
  drop_off: "drop_off_available",
  community: "community_program",
  favorites: "family_favorite",
  featured: "featured",
};

// URL token ↔ `availability_mode` menu value (the UI presents `anytime` as
// "Flexible"; see ProgramsFiltersAdapter).
const AVAILABILITY_TO_MODE: Record<string, string> = { sessions: "sessions", flexible: "anytime" };
const MODE_TO_AVAILABILITY: Record<string, string> = { sessions: "sessions", anytime: "flexible" };

const FORMAT_VALUES: string[] = FORMAT_OPTIONS.map((option) => option.value);
const INFLUENCE_VALUES = INFLUENCE_FILTERS.map((filter) => filter.value);

// The instruction-languages facet carries every code the corpus contains, not
// just the curated modal set — validate by shape, not allowlist.
const LANGUAGE_SHAPE = /^[a-z]{2}$/;

function parseLanguages(raw: string | null): string[] {
  if (!raw) return [];
  return [...new Set(raw.split(",").filter((code) => LANGUAGE_SHAPE.test(code)))].sort();
}

/** The URL's filter params as `initialUiState`; `undefined` when none present. */
export function toProgramsUiState(url: string, indexName: string): UiState | undefined {
  const { params } = splitUrl(url);

  const toggle: Record<string, boolean> = {};
  for (const token of parseCanonicalList(params.get(RECOMMENDED_PARAM), RECOMMENDED_TOKENS)) {
    toggle[TOKEN_TO_TOGGLE[token]] = true;
  }
  const mode = AVAILABILITY_TO_MODE[params.get(AVAILABILITY_PARAM) ?? ""];
  const formats = parseCanonicalList(params.get(FORMATS_PARAM), FORMAT_VALUES);
  const influences = parseCanonicalList(params.get(INFLUENCES_PARAM), INFLUENCE_VALUES);
  const languages = parseLanguages(params.get(LANGUAGES_PARAM));

  const indexUiState: IndexUiState = {};
  if (Object.keys(toggle).length > 0) indexUiState.toggle = toggle;
  if (mode) indexUiState.menu = { availability_mode: mode };
  const refinementList: Record<string, string[]> = {};
  if (formats.length > 0) refinementList.format = formats;
  if (influences.length > 0) refinementList.influences = influences;
  if (languages.length > 0) refinementList.instruction_languages = languages;
  if (Object.keys(refinementList).length > 0) indexUiState.refinementList = refinementList;

  if (Object.keys(indexUiState).length === 0) return undefined;
  return { [indexName]: indexUiState };
}

/** The filter slice of the URL params, from live uiState. */
export function serializeProgramsFilterParams(indexUiState: IndexUiState): Record<string, string | undefined> {
  const refined = RECOMMENDED_TOKENS.filter((token) => indexUiState.toggle?.[TOKEN_TO_TOGGLE[token]]);
  const mode = indexUiState.menu?.availability_mode;
  const formats = (indexUiState.refinementList?.format ?? []).filter((value) => FORMAT_VALUES.includes(value));
  const influences = (indexUiState.refinementList?.influences ?? []).filter((value) =>
    INFLUENCE_VALUES.includes(value),
  );
  const languages = (indexUiState.refinementList?.instruction_languages ?? []).filter((code) =>
    LANGUAGE_SHAPE.test(code),
  );

  return {
    [AVAILABILITY_PARAM]: mode ? MODE_TO_AVAILABILITY[mode] : undefined,
    [RECOMMENDED_PARAM]: serializeCanonicalList(refined, RECOMMENDED_TOKENS),
    [FORMATS_PARAM]: serializeCanonicalList(formats, FORMAT_VALUES),
    [INFLUENCES_PARAM]: serializeCanonicalList(influences, INFLUENCE_VALUES),
    [LANGUAGES_PARAM]: languages.length > 0 ? [...languages].sort().join(",") : undefined,
  };
}

/** Whether the URL carries any programs-owned search param (v2 or legacy). */
export function hasProgramsSearchParams(url: string): boolean {
  const { params } = splitUrl(url);
  return PROGRAMS_SEARCH_PARAM_KEYS.some((key) => params.has(key));
}
